import { Component, DestroyRef, ElementRef, inject, output } from "@angular/core";
import { CdkDrag } from "@angular/cdk/drag-drop";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DragAndDropService } from "../drag-and-drop.service";
import { fromEvent } from "rxjs";
import { auditTime, filter, switchMap, take, takeUntil, tap } from "rxjs/operators";

const DRAG_ATTEMPT_THRESHOLD_PX = 5;

@Component({
    selector: "wk-drag",
    templateUrl: "./drag.component.html",
    styleUrls: ["./drag.component.scss"],
    host: {
        class: "wk-drag",
    },
    hostDirectives: [
        {
            directive: CdkDrag,
            inputs: ["cdkDragData", "cdkDragDisabled", "cdkDragPreviewContainer"],
            outputs: ["cdkDragStarted", "cdkDragEnded", "cdkDragMoved"],
        },
    ],
    standalone: true,
})
export class DragComponent {
    private readonly drag = inject(CdkDrag);
    private readonly destroyRef = inject(DestroyRef);
    private readonly dragAndDropService = inject(DragAndDropService);
    private readonly elementRef = inject(ElementRef<HTMLElement>);

    readonly dragAttempted = output<void>();

    constructor() {
        this.drag.started.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((e) => this.dragAndDropService.onDragStarted(e));
        this.drag.moved.pipe(auditTime(15), takeUntilDestroyed(this.destroyRef)).subscribe((e) => this.dragAndDropService.onDragMoved(e));

        this.setupDragAttemptDetection();
    }

    private setupDragAttemptDetection() {
        const el = this.elementRef.nativeElement;
        let dragGestureOccurred = false;

        fromEvent<PointerEvent>(el, "pointerdown")
            .pipe(
                tap(() => {
                    dragGestureOccurred = false;
                }),
                filter(() => this.drag.disabled),
                switchMap((down) =>
                    fromEvent<PointerEvent>(document, "pointermove").pipe(
                        filter((move) => {
                            const dx = move.clientX - down.clientX;
                            const dy = move.clientY - down.clientY;
                            return dx * dx + dy * dy > DRAG_ATTEMPT_THRESHOLD_PX * DRAG_ATTEMPT_THRESHOLD_PX;
                        }),
                        take(1),
                        takeUntil(fromEvent(document, "pointerup")),
                    ),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                dragGestureOccurred = true;
                this.dragAttempted.emit();
            });

        fromEvent<MouseEvent>(el, "click", { capture: true })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((e) => {
                if (dragGestureOccurred) {
                    e.stopPropagation();
                    dragGestureOccurred = false;
                }
            });
    }
}
